"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckboxRow,
  TextInput,
  PrintButton,
  CopyButton,
  TableScroll,
  PrintFooter,
  trackToolResult,
} from "@/components/tools/ui";

/* PPE selector — maps selected tasks/hazards to a consolidated PPE specification
   with the correct current BS EN / EN ISO standards.

   FRAMING: PPE is the LAST resort in the hierarchy of control. This tool assumes
   elimination, substitution, engineering and administrative controls have already
   been applied and a residual risk remains. Several items therefore carry an
   explicit "X is not a substitute for Y" line so the schedule cannot be read as
   permission to skip higher-order controls.

   Standards cited (all current BS EN / EN ISO at June 2026):
     head      EN 397 (industrial helmet) / EN 12492 (mountaineering-type, work at height)
     eye       EN 166 (with field-of-use grade), welding EN 169 filters + EN 175 face
     hearing   EN 352
     gloves    EN 388 (mechanical/cut), EN 374 (chemical), EN 407 (heat)
     footwear  EN ISO 20345 (S3 etc.)
     hi-vis    EN ISO 20471 (class 1/2/3)
     RPE       EN 149 (FFP3 disposable), EN 14387 (cartridges) — face-fit required
     harness   EN 361 + lanyard EN 354 / EN 355 (energy-absorbing)
     FR cloth. EN ISO 11611 (welding) / EN ISO 11612 (general flame/heat) */

interface Hazard {
  id: string;
  label: string;
  group: string;
  /** Short note on the higher-order control PPE must not replace. */
  hint?: string;
}

const HAZARDS: Hazard[] = [
  { id: "cutting", group: "Cutting & grinding", label: "Cutting, grinding or abrasive wheels", hint: "Guard, on-tool extraction and dust suppression first." },
  { id: "drilling", group: "Drilling & breaking", label: "Drilling, chasing or breaking out", hint: "Water suppression / on-tool extraction first." },
  { id: "height", group: "Work at height", label: "Work at height (fall risk above ~2 m or where any fall could injure)", hint: "Collective protection (guardrails, MEWP, nets) before fall arrest." },
  { id: "hot", group: "Hot works", label: "Hot works — welding, cutting, grinding sparks, brazing", hint: "Permit, fire watch and screening first." },
  { id: "chem", group: "Chemicals / COSHH", label: "Chemicals, solvents, resins or other COSHH substances", hint: "Substitute, contain and ventilate before relying on PPE." },
  { id: "dust", group: "Dust (silica / wood)", label: "Hazardous dust — RCS (silica), wood or other fine dust", hint: "Eliminate, suppress or extract at source first." },
  { id: "noise", group: "Noise", label: "Noisy work at or above the action values", hint: "Quieter methods and enclosure before hearing protection." },
  { id: "electrical", group: "Electrical work", label: "Electrical work / live or potentially-live systems", hint: "Isolate and prove dead — work dead wherever possible." },
  { id: "lifting", group: "Lifting & handling", label: "Manual handling / lifting loads", hint: "Mechanical aids and load reduction before PPE." },
  { id: "site", group: "Site presence / traffic", label: "General site presence, plant or vehicle movements nearby", hint: "Segregation and traffic management before hi-vis." },
  { id: "wet", group: "Wet trades / cement", label: "Wet trades — concrete, screed, mortar, cement contact", hint: "Mixing controls and skin-care regime alongside PPE." },
  { id: "confined", group: "Confined spaces", label: "Confined space entry", hint: "Avoid entry; if unavoidable, atmosphere control and rescue plan first." },
];

/* Group order for rendering (matches the spec). */
const GROUP_ORDER = Array.from(new Set(HAZARDS.map((h) => h.group)));

interface PpeItem {
  /** Stable key so multiple hazards can converge on one item. */
  key: string;
  item: string;
  standard: string;
  /** Per-hazard reason fragments, keyed by hazard id. */
  reasons: Record<string, string>;
  /** Face-fit / training / limitation note shown in the schedule. */
  notes?: string;
  /** "X is not a substitute for Y" caveat (rendered prominently). */
  notSubstitute?: string;
}

/* Each rule contributes/merges a PPE item for one or more hazards. Reasons from
   several hazards are concatenated so the schedule shows everything that drove it. */
function buildSchedule(selected: Set<string>): PpeItem[] {
  const items = new Map<string, PpeItem>();

  const add = (
    base: Omit<PpeItem, "reasons">,
    hazardId: string,
    reason: string
  ) => {
    if (!selected.has(hazardId)) return;
    const existing = items.get(base.key);
    if (existing) {
      existing.reasons[hazardId] = reason;
      // Keep the richest note/caveat if a later rule supplies one.
      if (base.notes && !existing.notes) existing.notes = base.notes;
      if (base.notSubstitute && !existing.notSubstitute) existing.notSubstitute = base.notSubstitute;
    } else {
      items.set(base.key, { ...base, reasons: { [hazardId]: reason } });
    }
  };

  // ── Head protection ──────────────────────────────────────────────────────
  const headBase = {
    key: "head",
    item: "Safety helmet",
    standard: "BS EN 397 (industrial helmet)",
    notes: "Replace after any significant impact; check shell and harness before use.",
  };
  add(headBase, "cutting", "overhead and flying-object risk from grinding/cutting");
  add(headBase, "drilling", "falling material when breaking out or chasing");
  add(headBase, "site", "general site head-injury risk from plant, loads and overhead work");
  add(headBase, "lifting", "head impact risk around suspended or moving loads");
  add(headBase, "confined", "impact risk in restricted-headroom spaces");

  // Work at height upgrades the helmet to a retained, chin-strap type.
  add(
    {
      key: "head",
      item: "Safety helmet (retained, chin strap)",
      standard: "BS EN 397 with chin strap, or BS EN 12492 (mountaineering-type)",
      notes: "Chin strap must be worn done up at height so the helmet is retained in a fall or trip.",
    },
    "height",
    "helmet must stay on the head during a fall — a strapless EN 397 helmet can be lost"
  );

  // ── Eye protection ───────────────────────────────────────────────────────
  add(
    {
      key: "eye",
      item: "Safety spectacles / goggles",
      standard: "BS EN 166 — grade B impact, marked 3 for liquids where wet cutting",
      notes: "Goggles (not spectacles) where high-velocity particles or dust ingress is likely.",
    },
    "cutting",
    "high-velocity fragments and abrasive wheel debris"
  );
  add(
    {
      key: "eye",
      item: "Safety goggles",
      standard: "BS EN 166 — grade B impact, with dust (5) marking",
      notes: "Sealed goggles to keep fine dust out; spectacles are insufficient for fine dust.",
    },
    "dust",
    "fine airborne dust irritating and damaging the eyes"
  );
  add(
    {
      key: "eye",
      item: "Safety goggles (chemical splash)",
      standard: "BS EN 166 — marked 3 (liquid droplets/splash)",
      notes: "Pair with a face shield where a splash to the face is foreseeable.",
    },
    "chem",
    "chemical splash to the eyes"
  );
  add(
    {
      key: "eye",
      item: "Impact eye protection",
      standard: "BS EN 166 — grade B impact",
    },
    "drilling",
    "flying debris and spoil when drilling/breaking"
  );

  // Welding eye/face protection is a distinct, higher specification.
  add(
    {
      key: "welding-eye",
      item: "Welding face/eye protection",
      standard: "BS EN 175 (welding face protection) with BS EN 169 filter (correct shade)",
      notes: "Select the filter shade for the process and current. Auto-darkening to EN 379 acceptable. Others nearby need screening / EN 169 protection too.",
      notSubstitute: "A welding shield is not a substitute for screening the work area to protect others from arc eye.",
    },
    "hot",
    "intense optical radiation (arc eye) and spatter during welding/cutting"
  );

  // ── Hearing protection ───────────────────────────────────────────────────
  const hearingBase = {
    key: "hearing",
    item: "Hearing protection (ear defenders / plugs)",
    standard: "BS EN 352",
    notes: "Match the SNR/attenuation to the measured noise level — over-protection causes isolation and removal.",
    notSubstitute: "Hearing protection is not a substitute for reducing noise at source or limiting exposure time.",
  };
  add(hearingBase, "noise", "noise exposure at or above the action values");
  add(hearingBase, "cutting", "high noise from cutting/grinding tools");
  add(hearingBase, "drilling", "high noise from breakers and percussive tools");

  // ── Hand protection ──────────────────────────────────────────────────────
  add(
    {
      key: "gloves-cut",
      item: "Cut-resistant gloves",
      standard: "BS EN 388 — select cut level for the task (e.g. cut C/D)",
      notes: "Do not wear loose gloves near rotating parts (drills, grinders) — entanglement risk.",
    },
    "cutting",
    "cut and abrasion risk handling materials and blades"
  );
  add(
    {
      key: "gloves-grip",
      item: "General handling gloves",
      standard: "BS EN 388 (mechanical risks)",
    },
    "lifting",
    "abrasion, cuts and crush points when handling loads"
  );
  add(
    {
      key: "gloves-grip",
      item: "General handling gloves",
      standard: "BS EN 388 (mechanical risks)",
    },
    "drilling",
    "abrasion and sharp-edge contact handling tools and spoil"
  );
  add(
    {
      key: "gloves-chem",
      item: "Chemical-resistant gloves",
      standard: "BS EN 374 — select the material/breakthrough class for the specific substance",
      notes: "Check the COSHH assessment / SDS for the right glove material and breakthrough time; not all gloves resist all chemicals.",
      notSubstitute: "Gloves are not a substitute for substituting the substance or controlling exposure at source.",
    },
    "chem",
    "skin contact with hazardous substances"
  );
  add(
    {
      key: "gloves-cement",
      item: "Waterproof, alkali-resistant gloves",
      standard: "BS EN 388 + BS EN 374 (waterproof, alkali-resistant)",
      notes: "Wet cement is highly alkaline and causes burns; keep cement off skin and follow a skin-care regime.",
    },
    "wet",
    "cement burns and dermatitis from wet concrete/mortar contact"
  );
  add(
    {
      key: "gloves-heat",
      item: "Heat / welding gloves",
      standard: "BS EN 407 (thermal) and BS EN 12477 for welding",
      notes: "Gauntlet-style to protect the wrist/forearm from spatter and radiant heat.",
    },
    "hot",
    "contact heat, radiant heat and spatter during hot works"
  );
  add(
    {
      key: "gloves-elec",
      item: "Insulating gloves (only if live work is justified)",
      standard: "BS EN 60903 — class rated for the voltage",
      notes: "Live work must be exceptional and justified; insulating gloves require inspection/test before use.",
      notSubstitute: "Insulating gloves are not a substitute for isolation and proving dead — work dead wherever possible.",
    },
    "electrical",
    "contact with live or potentially-live conductors where work cannot be done dead"
  );

  // ── Foot protection ──────────────────────────────────────────────────────
  const footBase = {
    key: "foot",
    item: "Safety footwear",
    standard: "BS EN ISO 20345 — S3 (toe cap, midsole penetration, water-resistant)",
    notes: "S3 for general construction ground; check soles for nail-penetration resistance.",
  };
  add(footBase, "site", "general site foot hazards — punctures, crushing, uneven ground");
  add(footBase, "cutting", "dropped material and sharp offcuts");
  add(footBase, "lifting", "crush risk to feet from dropped loads");
  add(footBase, "drilling", "puncture and impact risk from spoil and tools");
  add(
    {
      key: "foot",
      item: "Safety wellingtons (waterproof)",
      standard: "BS EN ISO 20345 — S5 (waterproof, midsole, toe cap)",
      notes: "Waterproof footwear for prolonged contact with wet concrete; rinse cement off promptly.",
    },
    "wet",
    "prolonged contact with wet, alkaline cement and standing water"
  );

  // ── High-visibility clothing ─────────────────────────────────────────────
  add(
    {
      key: "hivis",
      item: "High-visibility clothing",
      standard: "BS EN ISO 20471 — Class 2 minimum (Class 3 near roads / high-speed plant)",
      notes: "Keep clean and serviceable; faded or contaminated hi-vis loses its certification.",
      notSubstitute: "Hi-vis is not a substitute for segregating people from plant and vehicles.",
    },
    "site",
    "being seen by plant operators and drivers on site"
  );

  // ── Respiratory protection (RPE) ─────────────────────────────────────────
  add(
    {
      key: "rpe-dust",
      item: "Respiratory protection (RPE) — FFP3",
      standard: "BS EN 149 FFP3 (disposable) or reusable half-mask with P3 filter (BS EN 14387)",
      notes: "Face-fit test required for tight-fitting RPE before first use; user must be clean-shaven at the seal. Train in fitting and storage.",
      notSubstitute: "FFP3 is not a substitute for dust extraction / water suppression — control silica and fine dust at source first.",
    },
    "dust",
    "respirable crystalline silica / fine dust — high-toxicity inhalation risk"
  );
  add(
    {
      key: "rpe-chem",
      item: "Respiratory protection (RPE) — correct filter for the substance",
      standard: "BS EN 14387 cartridge (A/B/E/K/P as appropriate) on a tight-fitting mask",
      notes: "Filter type must match the COSHH assessment / SDS. Face-fit test required for tight-fitting masks; clean-shaven at the seal.",
      notSubstitute: "RPE is not a substitute for ventilation, containment or substitution of the substance.",
    },
    "chem",
    "inhalation of vapours, fumes or mists from hazardous substances"
  );
  add(
    {
      key: "rpe-weld",
      item: "Welding fume RPE",
      standard: "BS EN 149 FFP3, powered air or supplied-air per the fume risk",
      notes: "All welding fume is a carcinogen — LEV is required indoors and outdoors where it is reasonably practicable. Face-fit any tight-fitting RPE.",
      notSubstitute: "Welding-fume RPE is not a substitute for local exhaust ventilation (LEV) at the arc.",
    },
    "hot",
    "welding fume — a recognised carcinogen requiring respiratory protection in addition to LEV"
  );

  // ── Fall-arrest equipment ────────────────────────────────────────────────
  add(
    {
      key: "harness",
      item: "Fall-arrest harness and energy-absorbing lanyard",
      standard: "Harness BS EN 361; lanyard BS EN 354 with energy absorber BS EN 355",
      notes: "Requires a suitable anchor point, adequate clearance below, training, pre-use inspection and a rescue plan. Personal fall protection only after collective protection is shown impracticable.",
      notSubstitute: "A harness is not a substitute for collective fall protection (guardrails, working platforms, MEWPs).",
    },
    "height",
    "arresting a fall where collective protection cannot fully control the risk"
  );

  // ── Flame-resistant / heat-protective clothing ───────────────────────────
  add(
    {
      key: "fr-cloth",
      item: "Flame-resistant clothing",
      standard: "BS EN ISO 11611 (welding) — also BS EN ISO 11612 for general flame/heat",
      notes: "Natural-fibre or certified FR garments only; no synthetic clothing that can melt. Cover exposed skin against UV and spatter.",
    },
    "hot",
    "ignition risk and burns from spatter, sparks and radiant heat"
  );

  // ── Confined space additions ─────────────────────────────────────────────
  add(
    {
      key: "rpe-confined",
      item: "Respiratory protection for confined entry (often supplied-air / SCBA)",
      standard: "Supplied-air BS EN 14593/14594 or SCBA BS EN 137 where the atmosphere may be deficient or toxic",
      notes: "Filtering RPE does NOT protect against oxygen deficiency. Atmosphere testing, escape sets, harness and a rescue plan are required.",
      notSubstitute: "RPE is not a substitute for testing and controlling the atmosphere and having a workable rescue plan.",
    },
    "confined",
    "potential oxygen deficiency or toxic atmosphere in a confined space"
  );
  add(
    {
      key: "harness-confined",
      item: "Rescue harness (confined space)",
      standard: "BS EN 361 with retrieval / rescue attachment points",
      notes: "Used with a retrieval line and man-riding winch where vertical rescue may be needed; rescue must be planned before entry.",
    },
    "confined",
    "to allow retrieval/rescue of an entrant from the space"
  );

  // ── Electrical clothing/face note ────────────────────────────────────────
  add(
    {
      key: "arc-face",
      item: "Arc-rated face/eye protection and FR clothing (if arc-flash risk)",
      standard: "Arc-rated PPE to BS EN 61482 series where an arc-flash risk remains",
      notes: "Only where live working is justified and an arc-flash risk has been assessed.",
      notSubstitute: "Arc-rated PPE is not a substitute for isolation, proving dead and safe-system-of-work for electrical tasks.",
    },
    "electrical",
    "residual arc-flash burn risk where live working cannot be avoided"
  );

  // Preserve a sensible display order.
  const ORDER = [
    "head", "eye", "welding-eye", "hearing",
    "gloves-cut", "gloves-grip", "gloves-chem", "gloves-cement", "gloves-heat", "gloves-elec",
    "foot", "hivis", "fr-cloth",
    "rpe-dust", "rpe-chem", "rpe-weld", "rpe-confined",
    "harness", "harness-confined", "arc-face",
  ];
  return Array.from(items.values()).sort(
    (a, b) => (ORDER.indexOf(a.key) + 1 || 99) - (ORDER.indexOf(b.key) + 1 || 99)
  );
}

function hazardLabel(id: string): string {
  return HAZARDS.find((h) => h.id === id)?.label ?? id;
}

/* Known PPE combinations to check for compatibility — each rule fires when both
   item keys appear in the schedule. Tight-fitting RPE matches any rpe-* key. */
const TIGHT_RPE = ["rpe-dust", "rpe-chem", "rpe-weld", "rpe-confined"];
interface CompatRule {
  a: string[];
  b: string[];
  message: string;
}
const COMPAT: CompatRule[] = [
  {
    a: TIGHT_RPE,
    b: ["eye"],
    message:
      "Tight-fitting RPE + safety spectacles/goggles: spectacle arms or a goggle seal can break the mask face-seal. Use RPE-compatible eye protection (or a full-face mask) and confirm the seal at the face-fit test.",
  },
  {
    a: ["head"],
    b: ["hearing"],
    message:
      "Helmet + ear defenders: some helmet-mounted or over-the-head defenders do not fit every helmet. Use helmet-mounted defenders rated for that shell, or in-ear plugs, and confirm both are still certified together.",
  },
  {
    a: TIGHT_RPE,
    b: ["welding-eye"],
    message:
      "Tight-fitting RPE + face shield / welding face protection: a shield or visor can disturb the mask seal. Check compatibility, or use powered/supplied-air RPE designed to integrate with the shield.",
  },
];

function compatWarnings(keys: Set<string>): string[] {
  return COMPAT.filter(
    (r) => r.a.some((k) => keys.has(k)) && r.b.some((k) => keys.has(k)),
  ).map((r) => r.message);
}

export function PpeSelector() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [taskName, setTaskName] = useState("");
  const resultTracked = useRef(false);

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const schedule = useMemo(
    () => (selected.size > 0 ? buildSchedule(selected) : []),
    [selected]
  );

  const warnings = useMemo(
    () => compatWarnings(new Set(schedule.map((it) => it.key))),
    [schedule]
  );

  // Fire the funnel event once per visit when a schedule first appears.
  useEffect(() => {
    if (schedule.length > 0 && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("ppe-selector", { hazards: selected.size });
    }
  }, [schedule, selected]);

  return (
    <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
      {/* Hazard picker */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">What does the task involve?</h2>
        <p className="mt-1 text-sm text-muted">
          Tick every task or hazard that applies. The schedule on the right specifies the PPE — with the
          relevant BS EN standard — for the risks that remain after your higher-order controls.
        </p>

        <div className="mt-5 space-y-5">
          {GROUP_ORDER.map((group) => {
            const inGroup = HAZARDS.filter((h) => h.group === group);
            return (
              <div key={group}>
                <div className="mb-2 text-[0.78rem] font-bold uppercase tracking-wide text-muted">{group}</div>
                <div className="space-y-2">
                  {inGroup.map((h) => (
                    <CheckboxRow key={h.id} checked={selected.has(h.id)} onChange={(v) => toggle(h.id, v)}>
                      {h.label}
                      {h.hint && (
                        <span className="mt-0.5 block text-[0.78rem] text-muted">Control first: {h.hint}</span>
                      )}
                    </CheckboxRow>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5">
          <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">Task / activity name (for the printed schedule)</span>
          <TextInput
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            placeholder="e.g. Cutting and grinding kerbs, Block A"
          />
        </div>
      </div>

      {/* PPE schedule */}
      <div className="space-y-4">
        {schedule.length > 0 ? (
          <>
            <div className="print-area card shadow-soft p-6" id="ppe-schedule">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                PPE specification for inclusion in a RAMS
              </div>
              <h2 className="mt-1 text-lg">
                {taskName ? `${taskName} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              {/* Hierarchy-of-control lead note (always visible). */}
              <div className="mt-4 rounded-xl border border-amber-400/50 bg-[#fdf6e3] px-5 py-4 text-[0.92rem] leading-relaxed text-[#8a6400]">
                <div className="font-display text-base font-bold">PPE is the last line of defence — not the plan</div>
                <p className="mt-1">
                  This schedule assumes you have already eliminated, substituted, engineered out and
                  administratively controlled these hazards as far as is reasonably practicable. PPE only protects
                  the residual risk, depends on correct use, and protects one person at a time. Use it to top up
                  higher-order controls, never to replace them.
                </p>
              </div>

              <div className="mt-3 text-[0.82rem] text-muted">
                Selected tasks/hazards:{" "}
                <span className="text-body">
                  {Array.from(selected).map((id) => HAZARDS.find((h) => h.id === id)?.group).join(", ")}
                </span>
              </div>

              {/* Schedule table */}
              <div className="mt-4">
                <TableScroll>
                  <table className="w-full min-w-[680px] text-sm">
                  <thead>
                    <tr className="border-b border-line text-left align-bottom text-[0.74rem] uppercase tracking-wide text-muted">
                      <th className="py-1.5 pr-3 font-semibold">PPE item</th>
                      <th className="py-1.5 pr-3 font-semibold">Standard / specification</th>
                      <th className="py-1.5 pr-3 font-semibold">Why it&apos;s needed (triggering hazards)</th>
                      <th className="py-1.5 font-semibold">Face-fit / training notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((it) => (
                      <tr key={it.key} className="border-b border-line/60 align-top">
                        <td className="py-2.5 pr-3 font-semibold text-ink">{it.item}</td>
                        <td className="py-2.5 pr-3 text-body">{it.standard}</td>
                        <td className="py-2.5 pr-3 text-body">
                          <ul className="list-disc space-y-0.5 pl-4">
                            {Object.entries(it.reasons).map(([hid, reason]) => (
                              <li key={hid}>
                                <span className="font-medium text-ink">{hazardLabel(hid).split(" —")[0].split(" (")[0]}:</span>{" "}
                                {reason}
                              </li>
                            ))}
                          </ul>
                          {it.notSubstitute && (
                            <p className="mt-1.5 text-[0.82rem] font-semibold text-[#b34e10]">{it.notSubstitute}</p>
                          )}
                        </td>
                        <td className="py-2.5 text-[0.85rem] text-muted">{it.notes ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </TableScroll>
              </div>

              {/* Compatibility warnings — items that can compromise each other. */}
              {warnings.length > 0 && (
                <div className="mt-4 rounded-xl border border-risk-high/40 bg-[#fef0e6] px-5 py-4 text-[0.85rem] leading-relaxed text-[#b34e10]">
                  <div className="font-semibold">Check these combinations for compatibility</div>
                  <ul className="mt-1.5 list-disc space-y-1 pl-5">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Standing notes that apply to any PPE schedule. */}
              <div className="mt-4 rounded-xl border border-line bg-mist/60 px-5 py-4 text-[0.85rem] leading-relaxed text-body">
                <div className="font-semibold text-ink">Provision, fit and training</div>
                <ul className="mt-1.5 list-disc space-y-1 pl-5">
                  <li>
                    PPE must be provided free of charge to employees and to limb (b) workers (casual / agency /
                    most CIS sub-contractors) under the PPE at Work Regulations 2022, where the risk assessment
                    identifies a need.
                  </li>
                  <li>
                    Tight-fitting RPE (FFP3 and half/full masks) must be face-fit tested for each wearer before
                    first use, and the wearer must be clean-shaven at the seal.
                  </li>
                  <li>
                    Workers must be trained in how to fit, use, check and store each item, and understand its
                    limitations. PPE must be maintained, inspected and replaced when damaged or expired.
                  </li>
                  <li>
                    Where two items are worn together (e.g. RPE with eye protection or a helmet), check they are
                    compatible and do not compromise each other&apos;s seal or fit.
                  </li>
                </ul>
              </div>

              <p className="mt-4 text-[0.82rem] leading-relaxed text-muted">
                This is a starting specification to support competent-person review — not a complete risk
                assessment. Confirm grades, cut levels, filter types and standards against the actual task, the
                COSHH assessment and the manufacturers&apos; data before issuing.
              </p>

              <PrintFooter toolName="PPE selector" />
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <CopyButton
                tool="ppe-selector"
                label="Copy schedule as text"
                buildText={() => {
                  const lines: string[] = [];
                  lines.push(`PPE SCHEDULE${taskName.trim() ? ` — ${taskName.trim()}` : ""}`);
                  lines.push("");
                  schedule.forEach((it) => {
                    lines.push(`${it.item} — ${it.standard}`);
                    Object.entries(it.reasons).forEach(([hid, reason]) => {
                      lines.push(`  - ${hazardLabel(hid).split(" —")[0].split(" (")[0]}: ${reason}`);
                    });
                    if (it.notes) lines.push(`  Notes: ${it.notes}`);
                    lines.push("");
                  });
                  if (warnings.length > 0) {
                    lines.push("COMPATIBILITY — check these combinations:");
                    warnings.forEach((w) => lines.push(`  - ${w}`));
                    lines.push("");
                  }
                  lines.push(
                    "PPE is the last resort in the hierarchy of control — confirm elimination, engineering and administrative controls first. Provided free of charge; tight-fitting RPE requires face-fit testing.",
                  );
                  return lines.join("\n");
                }}
              />
              <PrintButton tool="ppe-selector" label="Print / save PPE schedule as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Tick the tasks and hazards on the left to build a consolidated PPE schedule — each item with its BS EN
            standard and the hazards that triggered it, ready to drop into your RAMS.
          </div>
        )}
      </div>
    </div>
  );
}
